import { clearingApi } from "../../config/apiConfig";

const BASE = "/api/v1/analytics/performance";

/**
 * CP-388 station performance analytics.
 *
 * These endpoints return 503 with `{ error: "NEVER_COMPUTED" }` for a month that has never been
 * aggregated, which is deliberately different from an empty result: "no data" and "never ran" are
 * different facts, and conflating them makes a reader conclude the fleet did nothing when the job
 * simply had not run. Every function below surfaces that as `neverComputed` rather than swallowing it.
 */

/** Serialises the shared reporting options into query params. */
const optionParams = ({
  unit = "SITE",
  segment = "ALL",
  moneyView = "TOTAL",
  includeFreeCharge = true,
  station = null,
  provider = null,
} = {}) => ({
  unit,
  segment,
  moneyView,
  includeFreeCharge,
  ...(station ? { station } : {}),
  ...(provider ? { provider } : {}),
});

/**
 * Normalises a failure into a shape the caller can render.
 *
 * A 503 NEVER_COMPUTED is an expected state with a specific remedy (run the backfill), not an error: * so it is distinguished from a genuine failure rather than both becoming "something went wrong".
 */
const toFailure = (error, context) => {
  console.error(`Failed to ${context}:`, error);
  const status = error?.response?.status;
  const body = error?.response?.data;
  if (status === 503 && body?.error === "NEVER_COMPUTED") {
    return {
      success: false,
      neverComputed: true,
      months: body?.months ?? [],
      message: "This month has not been computed yet.",
      hint: body?.hint ?? null,
    };
  }
  return {
    success: false,
    neverComputed: false,
    message: body?.error ?? "Could not load station performance.",
  };
};

/** Monthly scorecard: per-unit figures, baselines and month-over-month change. */
export const getMonthlyPerformance = async ({
  month,
  options,
  sort = "moneyCents",
  order = "desc",
  page = 1,
  limit = 50,
} = {}) => {
  try {
    const response = await clearingApi.get(BASE + "/monthly", {
      params: { month, sort, order, page, limit, ...optionParams(options) },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `load the monthly scorecard for ${month}`);
  }
};

/** Lifetime league table. `sort` accepts both the absolute and the per-active-month bases. */
export const getLifetimePerformance = async ({
  asOf,
  options,
  sort = "lifetime.moneyCents",
  order = "desc",
  includeNeverActive = false,
  page = 1,
  limit = 50,
} = {}) => {
  try {
    const response = await clearingApi.get(BASE + "/lifetime", {
      params: { asOf, sort, order, includeNeverActive, page, limit, ...optionParams(options) },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `load the lifetime league table as of ${asOf}`);
  }
};

/**
 * Pace-adjusted watchlist. `asOf` is optional and defaults to today server-side; pass it to reproduce
 * a past day.
 */
export const getWatchlist = async ({
  asOf = null,
  options,
  threshold = null,
  flaggedOnly = true,
} = {}) => {
  try {
    const response = await clearingApi.get(BASE + "/watchlist", {
      params: {
        ...(asOf ? { asOf } : {}),
        ...(threshold !== null ? { threshold } : {}),
        flaggedOnly,
        ...optionParams(options),
      },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, "load the underperformance watchlist");
  }
};

/**
 * Variance. `scope` is one of WITHIN_UNIT_SESSIONS, ACROSS_MONTHS, ACROSS_UNITS.
 *
 * A provider filter is rejected with 400 on any scope: the per-EMP breakdown carries counters only, so
 * a provider-scoped spread cannot be derived and returning the pooled one would be a lie.
 */
export const getDistribution = async ({
  scope,
  measure,
  month = null,
  unitId = null,
  from = null,
  to = null,
  options,
} = {}) => {
  try {
    const response = await clearingApi.get(BASE + "/distribution", {
      params: {
        scope,
        measure,
        ...(month ? { month } : {}),
        ...(unitId ? { unitId } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...optionParams(options),
      },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `load the ${scope} distribution`);
  }
};

/** Recompute a month range. Idempotent; sealed months in range are named back in the response. */
export const backfillPerformance = async ({ from, to }) => {
  try {
    const response = await clearingApi.post(BASE + "/backfill", { from, to });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `backfill ${from}..${to}`);
  }
};

/**
 * Run state per month. `checkDrift` also reports months whose aggregates have fallen behind the
 * source: for a sealed month that will not self-heal and needs a deliberate backfill.
 */
export const getPerformanceStatus = async ({ checkDrift = false } = {}) => {
  try {
    const response = await clearingApi.get(BASE + "/backfill/status", {
      params: { checkDrift },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, "load station performance run state");
  }
};

/**
 * Why money per active unit moved between two months (SC-006).
 *
 * `from` must be strictly before `to`. Steps arrive pre-ordered and pre-explained, so the client renders
 * them rather than deciding what each effect means.
 */
export const getDecomposition = async ({ from, to, options } = {}) => {
  try {
    const response = await clearingApi.get(BASE + "/decomposition", {
      params: { from, to, ...optionParams(options) },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `decompose the change from ${from} to ${to}`);
  }
};
